import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCalendarApp, ScheduleXCalendar } from "@schedule-x/react";
import {
  createViewDay,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
  viewMonthGrid,
} from "@schedule-x/calendar";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import { ACTIONS as CALENDAR_ACTIONS } from "../../redux/calendar";
import CalendarModal from "../calendar-modal";
import {
  addAttributeEventListeners,
  constructHourEvent,
  getClickPosition,
  getEventPosition,
} from "./utils";
import { config } from "./constants";

import StyledCalendar from "./styles";

import "@schedule-x/theme-default/dist/index.css";

function CalendarApp() {
  const dispatch = useDispatch();
  const plugins = [createEventsServicePlugin()];
  const [modalProps, setModalProps] = useState({ open: false });
  const { events: mainEvents } = useSelector((state) => state.calendar);

  const getLocalEvents = (isObject) => {
    let events = localStorage.getItem("calendar-events");

    if (events) {
      events = JSON.parse(events);
    } else {
      events = {};
    }

    if (isObject) {
      return events;
    }

    return Object.values(events);
  };

  const setSingleEvent = (event) => {
    let allEvents = getLocalEvents(true);

    allEvents[event.id] = event;
    localStorage.setItem("calendar-events", JSON.stringify(allEvents));
  };

  const setAllLocalEvents = (events) => {
    localStorage.setItem("calendar-events", JSON.stringify(events));
  };

  const initEvents = useMemo(() => {
    const events = getLocalEvents();
    return events || [];
  }, []);

  const [events, setEvents] = useState(initEvents);

  const calendar = useCalendarApp(
    {
      views: [
        createViewDay(),
        createViewWeek(),
        createViewMonthGrid(),
        createViewMonthAgenda(),
      ],
      locale: "es-ES",
      defaultView: viewMonthGrid.name,
      calendars: config.calendars,
    },
    plugins
  );

  const addProvisionalHourEvent = (date, elem, event) => {
    const hour = getClickPosition(elem, event);
    let id = crypto.randomUUID();

    const finalEvent = constructHourEvent(date, hour, id);

    setModalProps({
      open: true,
      event: finalEvent,
    });
  };

  const onMonthClickHandler = (date, elem, event) => {
    addProvisionalHourEvent(date, elem, event);
  };

  const onWeekClickHandler = (date, elem, event) => {
    addProvisionalHourEvent(date, elem, event);
  };

  const onEventClickHandler = useCallback(
    (id) => {
      let event = getLocalEvents(true);
      event = event[id];

      const isMainEvent = mainEvents.find((e) => e.id === id);

      if (event) {
        setModalProps({ open: true, event: event, isOldEvent: true });
      }
    },
    [mainEvents, getLocalEvents]
  );

  const addEventListeners = () => {
    addAttributeEventListeners("data-date", onMonthClickHandler);
    addAttributeEventListeners("data-event-id", onEventClickHandler);
    addAttributeEventListeners("data-time-grid-date", onWeekClickHandler);
  };

  const eventCalendarHandler = () => {
    const MutationObserver =
      window.MutationObserver || window.WebKitMutationObserver;

    const observer = new MutationObserver(addEventListeners);
    const calendar = document.querySelector(".sx__calendar-wrapper");

    if (calendar && observer) {
      observer.observe(calendar, {
        subtree: true,
        attributes: true,
      });
    }

    return () => observer.disconnect();
  };

  const closeModalHandler = () => {
    setModalProps({ open: false, event: null });
  };

  const onTitleChangeHandler = (e) => {
    setModalProps((p) => ({
      ...p,
      event: { ...p.event, title: e.target.value },
    }));
  };

  const onStartTimeChangeHandler = (time) => {
    setModalProps((p) => {
      const newStart = `${p.event.start.split(" ")[0]} ${time}`;
      return { ...p, event: { ...p.event, start: newStart } };
    });
  };

  const onEndTimeChangeHandler = (time) => {
    setModalProps((p) => {
      const newStart = `${p.event.end.split(" ")[0]} ${time}`;

      return { ...p, event: { ...p.event, end: newStart } };
    });
  };

  const onSingleDayChangeHandler = (date) => {
    setModalProps((p) => {
      const { start, end } = p.event;

      const newStart = `${date} ${start.split(" ")[1]}`;
      const newEnd = `${date} ${end.split(" ")[1]}`;

      return {
        ...p,
        event: { ...p.event, start: newStart, end: newEnd },
      };
    });
  };

  const onSaveHandler = () => {
    setSingleEvent(modalProps.event);
    const newEvents = getLocalEvents();
    setEvents(newEvents);
    setModalProps({ event: null, open: false });
  };

  const onFullDayStartDateChangeHandler = (time) => {
    setModalProps((p) => {
      return {
        ...p,
        event: {
          ...p.event,
          start: `${time}`,
        },
      };
    });
  };

  const onFullDayEndDateChangeHandler = (date) => {
    setModalProps((p) => {
      return {
        ...p,
        event: {
          ...p.event,
          end: `${date}`,
        },
      };
    });
  };

  const deleteEventHandler = (event) => {
    const id = event?.id;
    const events = getLocalEvents(true);
    delete events[id];
    setAllLocalEvents(events);
    setEvents(Object.values(events));
    setModalProps({ event: null, open: false });
  };

  useEffect(() => {
    let allEvents = events;

    if (modalProps?.event && !modalProps?.isOldEvent) {
      allEvents = [...events, modalProps.event];
    }

    allEvents = [...allEvents, ...mainEvents];

    calendar.eventsService.set(allEvents);
    addEventListeners(allEvents);
  }, [modalProps, calendar, events, mainEvents]);

  useEffect(() => {
    let disconnect = () => {};

    setTimeout(() => {
      disconnect = eventCalendarHandler();
      addEventListeners();
    }, 500);

    return () => {
      disconnect();
    };
  }, []);

  useEffect(() => {
    dispatch(CALENDAR_ACTIONS.getMainEvents());
  }, []);

  return (
    <>
      <CalendarModal
        {...modalProps}
        onClose={onSaveHandler}
        onCancel={closeModalHandler}
        onTitleChange={onTitleChangeHandler}
        onStartTimeChange={onStartTimeChangeHandler}
        onEndTimeChange={onEndTimeChangeHandler}
        onSingleDayChange={onSingleDayChangeHandler}
        onSave={onSaveHandler}
        onFullDayStartDateChange={onFullDayStartDateChangeHandler}
        onFullDayEndDateChange={onFullDayEndDateChangeHandler}
        deleteButton={modalProps?.isOldEvent}
        onDelete={deleteEventHandler}
      />

      <StyledCalendar>
        <div className="calendar-component">
          <ScheduleXCalendar calendarApp={calendar} />
        </div>
      </StyledCalendar>
    </>
  );
}

export default CalendarApp;
